import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { SearchGroups, SearchResponse } from "shared";
import { api } from "../api";
import { whyText } from "../search/WhyMatched";
import { STATES } from "../states";

/**
 * How search reads a query, for admins tuning synonyms and rule weights: words, corrections, synonyms and rules
 * applied, and the top results with their score, state details and why they matched (optionally for a caller's state).
 */
export function QueryTester() {
  const [text, setText] = useState("");
  const [state, setState] = useState("");
  const [run, setRun] = useState<{ q: string; state: string } | null>(null);
  const res = useQuery({
    queryKey: ["search", "tester", run?.q ?? "", run?.state ?? ""], enabled: !!run?.q, staleTime: 0,
    queryFn: () => api<SearchResponse>(`/api/search?${new URLSearchParams(run!.state ? { q: run!.q, state: run!.state } : { q: run!.q })}`),
  });
  const d = res.data;
  const row = (label: string, value: string) => <><dt className="text-slate-500">{label}</dt><dd>{value || "—"}</dd></>;
  const top = (r: SearchResponse, key: keyof SearchGroups, title: string) => (
    <div>
      <h4 className="label mt-3">{title}</h4>
      {r.groups[key].length === 0 ? <p className="text-xs text-slate-500">None</p> : (
        <ol className="list-decimal pl-5 text-xs">{r.groups[key].slice(0, 5).map((h) => (
          <li key={`${h.type}-${h.id}`} className="py-0.5">
            <span className="font-medium">{h.title}</span>
            <span className="text-slate-500"> · score {h.score}{h.stateCode ? ` · ${h.stateCode} details` : ""}{h.status === "discontinued" ? " · discontinued (always last)" : ""}</span>
            <div className="text-slate-500">{whyText(h.why) || "no words matched (linked to a matched problem)"}</div>
          </li>
        ))}</ol>
      )}
    </div>
  );
  return (
    <section className="card">
      <h3 className="mb-2 text-sm font-semibold">Test a query</h3>
      <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); setRun({ q: text.trim(), state }); }}>
        <input className="input min-w-0 flex-1" aria-label="Test query" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. lost job behind on rent" />
        <select className="input w-44" aria-label="Caller's state for the test" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">Any state</option>
          {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
        <button className="btn-primary">Test</button>
      </form>
      {res.error && <p role="alert" className="mt-2 text-sm text-red-600">{(res.error as Error).message}</p>}
      {d && (
        <>
          <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            {row("Words", d.query.tokens.join(", "))}
            {row("Corrections", d.query.corrections.map((c) => `${c.from} → ${c.to}`).join(", "))}
            {row("Synonyms used", d.query.synonyms.map((s) => `${s.from} → ${s.to}`).join("; "))}
            {row("Problems matched", d.query.problems.map((p) => `${p.title} (${p.rule})`).join("; "))}
          </dl>
          {top(d, "problems", "Top problems")}
          {top(d, "programs", "Top programs")}
        </>
      )}
    </section>
  );
}
